import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, Save, Scale, AlertTriangle, CheckCircle, Trash2, Plus, Upload } from 'lucide-react';
import Navbar from '@/components/Navbar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to fix image URLs that point to old/different preview domains
const fixImageUrl = (url) => {
  if (!url) return null;
  // Skip data URLs
  if (url.startsWith('data:')) return url;
  // If URL contains any emergentagent domain OR getmealguide domain, normalize to current BACKEND_URL
  if (url.includes('emergentagent.com/api/') || url.includes('getmealguide.com/api/')) {
    const pathMatch = url.match(/\/api\/uploads\/.+$/);
    if (pathMatch) {
      return `${BACKEND_URL}${pathMatch[0]}`;
    }
  }
  return url;
};

export default function RecipeEditor({ user, onLogout }) {
  const { recipeId } = useParams();
  const navigate = useNavigate();
  
  const [recipe, setRecipe] = useState(null);
  const [foodBankItems, setFoodBankItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Scaling
  const [showScaleDialog, setShowScaleDialog] = useState(false);
  const [targetCalories, setTargetCalories] = useState('');
  const [scalePreview, setScalePreview] = useState(null);
  const [scaling, setScaling] = useState(false);
  
  // Add Ingredient
  const [showAddIngredient, setShowAddIngredient] = useState(false);
  const [newIngredientFoodId, setNewIngredientFoodId] = useState('');
  const [newIngredientGrams, setNewIngredientGrams] = useState('100');
  const [adding, setAdding] = useState(false);
  
  // Image Upload
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchRecipe();
    fetchFoodBank();
  }, [recipeId]);

  const fetchRecipe = async () => {
    try {
      const response = await axios.get(`${API}/library-recipes/${recipeId}`);
      setRecipe(response.data);
      setTargetCalories(Math.round(response.data.total_kcal));
    } catch (error) {
      toast.error('Failed to load recipe');
      navigate('/coach/recipe-library');
    } finally {
      setLoading(false);
    }
  };

  const fetchFoodBank = async () => {
    try {
      const response = await axios.get(`${API}/food-bank?limit=500`);
      const items = response.data.foods || response.data || [];
      setFoodBankItems(items);
    } catch (error) {
      console.error('Failed to fetch Food Bank:', error);
    }
  };

  const updateIngredientGrams = async (ingredientIndex, newGrams) => {
    try {
      const ingredient = recipe.ingredients[ingredientIndex];
      const gramsValue = parseFloat(newGrams);
      
      // Validate input
      if (isNaN(gramsValue) || gramsValue <= 0) {
        toast.error('Please enter a valid positive number for grams');
        return;
      }
      
      const response = await axios.put(
        `${API}/library-recipes/${recipeId}/ingredients/${ingredientIndex}`,
        {
          food_bank_id: ingredient.food_bank_id,
          grams: gramsValue
        }
      );

      // Update local state
      const updatedRecipe = { ...recipe };
      updatedRecipe.ingredients[ingredientIndex] = response.data.ingredient;
      updatedRecipe.total_kcal = response.data.recipe_totals.kcal;
      updatedRecipe.total_protein = response.data.recipe_totals.protein_g;
      updatedRecipe.total_carbs = response.data.recipe_totals.carbs_g;
      updatedRecipe.total_fat = response.data.recipe_totals.fat_g;
      
      setRecipe(updatedRecipe);

      // Don't show toast - validation message is inline now

    } catch (error) {
      console.error('Update ingredient error:', error);
      
      // Handle different error types
      let errorMessage = 'Failed to update ingredient';
      
      if (error.response?.data?.detail) {
        if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        } else if (Array.isArray(error.response.data.detail)) {
          // Pydantic validation errors
          errorMessage = error.response.data.detail.map(e => e.msg).join(', ');
        }
      }
      
      toast.error(errorMessage);
    }
  };

  const swapIngredient = async (ingredientIndex, newFoodBankId) => {
    try {
      const currentGrams = recipe.ingredients[ingredientIndex].grams;
      
      const response = await axios.put(
        `${API}/library-recipes/${recipeId}/ingredients/${ingredientIndex}`,
        {
          food_bank_id: newFoodBankId,
          grams: currentGrams
        }
      );

      // Update local state
      const updatedRecipe = { ...recipe };
      updatedRecipe.ingredients[ingredientIndex] = response.data.ingredient;
      updatedRecipe.total_kcal = response.data.recipe_totals.kcal;
      updatedRecipe.total_protein = response.data.recipe_totals.protein_g;
      updatedRecipe.total_carbs = response.data.recipe_totals.carbs_g;
      updatedRecipe.total_fat = response.data.recipe_totals.fat_g;
      updatedRecipe.allergens = response.data.allergens || recipe.allergens || [];
      
      setRecipe(updatedRecipe);
      toast.success('Ingredient swapped');

    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to swap ingredient');
    }
  };

  const handleScaleRecipe = async () => {
    setScaling(true);
    try {
      const response = await axios.post(
        `${API}/library-recipes/${recipeId}/scale-to-calories`,
        { target_calories: parseFloat(targetCalories) }
      );

      setScalePreview(response.data);
      setShowScaleDialog(true);

    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to scale recipe');
    } finally {
      setScaling(false);
    }
  };

  const applyScaling = () => {
    // Apply scaled ingredients to recipe
    const updatedRecipe = { ...recipe };
    updatedRecipe.ingredients = scalePreview.scaled_ingredients.map((ing, idx) => ({
      ...recipe.ingredients[idx],
      ...ing,
      food_item_name: ing.food_bank_name,
      count_display: ing.display_text
    }));
    updatedRecipe.total_kcal = scalePreview.totals.kcal;
    updatedRecipe.total_protein = scalePreview.totals.protein_g;
    updatedRecipe.total_carbs = scalePreview.totals.carbs_g;
    updatedRecipe.total_fat = scalePreview.totals.fat_g;
    
    setRecipe(updatedRecipe);
    setShowScaleDialog(false);
    toast.success('Recipe scaled successfully');
  };

  const handleAddIngredient = async () => {
    if (!newIngredientFoodId || !newIngredientGrams) {
      toast.error('Please select an ingredient and enter grams');
      return;
    }

    const gramsValue = parseFloat(newIngredientGrams);
    if (isNaN(gramsValue) || gramsValue <= 0) {
      toast.error('Please enter a valid positive number for grams');
      return;
    }

    setAdding(true);
    try {
      const response = await axios.post(
        `${API}/library-recipes/${recipeId}/ingredients`,
        {
          food_bank_id: newIngredientFoodId,
          grams: gramsValue
        }
      );

      // Update recipe with new ingredient, allergens, and totals
      const updatedRecipe = { ...recipe };
      updatedRecipe.ingredients.push(response.data.ingredient);
      updatedRecipe.allergens = response.data.allergens || recipe.allergens || [];
      updatedRecipe.total_kcal = response.data.recipe_totals.kcal;
      updatedRecipe.total_protein = response.data.recipe_totals.protein_g;
      updatedRecipe.total_carbs = response.data.recipe_totals.carbs_g;
      updatedRecipe.total_fat = response.data.recipe_totals.fat_g;
      
      setRecipe(updatedRecipe);
      setShowAddIngredient(false);
      setNewIngredientFoodId('');
      setNewIngredientGrams('100');
      
      toast.success('Ingredient added successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add ingredient');
    } finally {
      setAdding(false);
    }
  };

  const deleteIngredient = async (ingredientIndex) => {
    try {
      const response = await axios.delete(
        `${API}/library-recipes/${recipeId}/ingredients/${ingredientIndex}`
      );

      // Update recipe with new totals and allergens
      const updatedRecipe = { ...recipe };
      updatedRecipe.ingredients.splice(ingredientIndex, 1);
      updatedRecipe.allergens = response.data.allergens || recipe.allergens || [];
      updatedRecipe.total_kcal = response.data.recipe_totals.kcal;
      updatedRecipe.total_protein = response.data.recipe_totals.protein_g;
      updatedRecipe.total_carbs = response.data.recipe_totals.carbs_g;
      updatedRecipe.total_fat = response.data.recipe_totals.fat_g;
      
      setRecipe(updatedRecipe);
      toast.success(`Removed ${response.data.removed_ingredient}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove ingredient');
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${API}/library-recipes/${recipeId}/upload-image`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      setRecipe({ ...recipe, image_url: response.data.image_url });
      toast.success('Image uploaded successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center h-screen">
          <p>Loading recipe...</p>
        </div>
      </div>
    );
  }

  if (!recipe) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar user={user} onLogout={onLogout} />
      
      <div className="max-w-5xl mx-auto p-6">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              onClick={() => navigate('/coach/recipe-library')}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div className="flex-1">
              <Input
                value={recipe.name}
                onChange={(e) => setRecipe({...recipe, name: e.target.value})}
                onBlur={async () => {
                  try {
                    await axios.patch(`${API}/library-recipes/${recipeId}/name`, {
                      name: recipe.name
                    });
                    toast.success('Recipe name updated');
                  } catch (error) {
                    toast.error('Failed to update recipe name');
                  }
                }}
                className="text-3xl font-bold border-none shadow-none px-0 focus:ring-0"
                placeholder="Recipe name..."
              />
              <p className="text-gray-600">{recipe.meal_type}</p>
            </div>
          </div>
          
          <div className="flex gap-2">
            <Button
              onClick={handleScaleRecipe}
              disabled={scaling}
              variant="outline"
            >
              <Scale className="w-4 h-4 mr-2" />
              Scale Recipe
            </Button>
          </div>
        </div>

        {/* Recipe Image */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Recipe Image</span>
              <p className="text-sm font-normal text-gray-500">Recommended: 800x600px, JPG/PNG, max 5MB</p>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              {recipe.image_url ? (
                <div className="relative w-40 h-40 bg-gray-100 rounded-lg flex items-center justify-center">
                  <img 
                    src={fixImageUrl(recipe.image_url)} 
                    alt={recipe.name}
                    className="w-full h-full object-contain rounded-lg"
                  />
                  <Button
                    size="sm"
                    variant="destructive"
                    className="absolute top-2 right-2"
                    onClick={() => setRecipe({ ...recipe, image_url: null })}
                  >
                    Remove
                  </Button>
                </div>
              ) : (
                <div className="w-48 h-36 bg-gray-100 rounded-lg flex items-center justify-center text-gray-400">
                  No image
                </div>
              )}
              <div className="flex-1">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  disabled={uploading}
                  className="hidden"
                  id="image-upload"
                />
                <Button
                  variant="outline"
                  disabled={uploading}
                  onClick={() => document.getElementById('image-upload').click()}
                  type="button"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {uploading ? 'Uploading...' : 'Upload Image'}
                </Button>
                <p className="text-xs text-gray-500 mt-2">
                  Images will be displayed at 800x600px for clients. For best results, upload images at this size or larger.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Recipe Totals */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="grid grid-cols-4 gap-4 text-center">
              <div>
                <p className="text-2xl font-bold text-[#1a61e8]">{Math.round(recipe.total_kcal)}</p>
                <p className="text-sm text-gray-600">Calories</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-600">{Math.round(recipe.total_protein)}g</p>
                <p className="text-sm text-gray-600">Protein</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-600">{Math.round(recipe.total_carbs)}g</p>
                <p className="text-sm text-gray-600">Carbs</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-red-600">{Math.round(recipe.total_fat)}g</p>
                <p className="text-sm text-gray-600">Fat</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Recipe Metadata */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg">Recipe Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Cooking Time - Editable Select */}
            <div>
              <Label className="text-sm font-semibold">Cooking Time</Label>
              <Select
                value={recipe.cooking_time || recipe.estimated_time || ''}
                onValueChange={async (value) => {
                  try {
                    await axios.put(`${API}/library-recipes/${recipe.id}/metadata`, {
                      cooking_time: value
                    });
                    setRecipe({ ...recipe, cooking_time: value, estimated_time: value });
                    toast.success('Cooking time updated');
                  } catch (error) {
                    toast.error('Failed to update cooking time');
                  }
                }}
              >
                <SelectTrigger className="w-48 mt-1">
                  <SelectValue placeholder="Select cooking time">
                    {recipe.cooking_time || recipe.estimated_time ? 
                      `⏱️ ${recipe.cooking_time || recipe.estimated_time}` : 
                      'Select cooking time'}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="5-15">⏱️ 5-15 min (Quick)</SelectItem>
                  <SelectItem value="15-30">⏱️ 15-30 min (Moderate)</SelectItem>
                  <SelectItem value="30+">⏱️ 30+ min (Extended)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                ℹ️ Used for filtering recipes by client cooking preferences
              </p>
            </div>

            {/* Diet Type */}
            {recipe.diet_type && (
              <div>
                <Label className="text-sm font-semibold">Diet Type</Label>
                <div className="mt-1">
                  <Badge variant="outline">{recipe.diet_type}</Badge>
                </div>
              </div>
            )}

            {/* Allergens - Auto-updated from ingredients */}
            <div>
              <Label className="text-sm font-semibold">Allergens (Auto-detected)</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {recipe.allergens && recipe.allergens.length > 0 ? (
                  recipe.allergens.map((allergen, idx) => (
                    <Badge key={idx} variant="destructive" className="text-xs">
                      {allergen}
                    </Badge>
                  ))
                ) : (
                  <p className="text-sm text-gray-500">No allergens detected</p>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                ℹ️ Allergens are automatically updated when you add, remove, or swap ingredients
              </p>
            </div>
            
            {/* Diet Categories - Manual Selection */}
            <div>
              <Label className="text-sm font-semibold">Diet Categories</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {['standard', 'vegetarian', 'vegan', 'pescatarian', 'keto', 'halal', 'gluten_free', 'high_protein', 'dairy_free'].map(diet => {
                  // Normalize comparison: check if any stored category matches when normalized
                  const isChecked = (recipe.dietary_categories || []).some(cat => 
                    cat.toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_') === diet
                  );
                  
                  return (
                    <label key={diet} className="flex items-center gap-2 cursor-pointer p-2 hover:bg-gray-50 rounded">
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={async (e) => {
                          // When updating, use normalized format
                          const currentNormalized = (recipe.dietary_categories || []).map(c => 
                            c.toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_')
                          );
                          const newCategories = e.target.checked
                            ? [...new Set([...currentNormalized, diet])]
                            : currentNormalized.filter(c => c !== diet);
                          
                          try {
                            await axios.put(`${API}/library-recipes/${recipe.id}/metadata`, {
                              dietary_categories: newCategories
                            });
                            setRecipe({ ...recipe, dietary_categories: newCategories });
                            toast.success(`Diet category ${e.target.checked ? 'added' : 'removed'}`);
                          } catch (error) {
                            toast.error('Failed to update diet categories');
                          }
                        }}
                        className="w-4 h-4"
                      />
                      <span className="text-sm capitalize">{diet.replace(/_/g, ' ')}</span>
                    </label>
                  );
                })}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                ℹ️ Select all diets that can eat this recipe
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Ingredients Editor */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Ingredients</CardTitle>
            <Button
              onClick={() => setShowAddIngredient(true)}
              variant="outline"
              size="sm"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Ingredient
            </Button>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recipe.ingredients.map((ing, idx) => (
                <div key={idx} className="border rounded-lg p-4 space-y-3">
                  <div className="grid grid-cols-12 gap-4 items-start">
                    {/* Food Bank Dropdown */}
                    <div className="col-span-5">
                      <Label className="text-xs text-gray-500">Ingredient</Label>
                      <Select
                        value={ing.food_bank_id}
                        onValueChange={(value) => swapIngredient(idx, value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select ingredient">
                            {ing.food_bank_name || foodBankItems.find(f => f.id === ing.food_bank_id)?.name || 'Select ingredient'}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent className="max-h-[300px]">
                          {foodBankItems.map(item => (
                            <SelectItem key={item.id} value={item.id}>
                              {item.name} ({item.category})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Grams Input */}
                    <div className="col-span-3">
                      <Label className="text-xs text-gray-500">Grams (editable)</Label>
                      <Input
                        type="number"
                        value={ing.grams}
                        onChange={(e) => updateIngredientGrams(idx, e.target.value)}
                        onBlur={(e) => updateIngredientGrams(idx, e.target.value)}
                        className="font-mono"
                      />
                      {/* Inline validation warning */}
                      {ing.validation_warning && typeof ing.validation_warning === 'string' && (
                        <p className="text-xs text-amber-600 mt-1 flex items-start gap-1">
                          <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                          <span>{ing.validation_warning}</span>
                        </p>
                      )}
                    </div>

                    {/* Unit Display (Helper) */}
                    <div className="col-span-4">
                      <Label className="text-xs text-gray-500">Unit Display</Label>
                      <div className="h-10 flex items-center text-sm text-gray-700 font-medium">
                        {ing.unit && ing.unit !== 'g' ? (
                          <span>≈ {ing.friendly_fraction || ing.quantity} {ing.unit}</span>
                        ) : ing.grams ? (
                          <span>({Math.round(ing.grams)}g)</span>
                        ) : (
                          <span>-</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Macros */}
                  <div className="flex gap-4 text-xs text-gray-600">
                    <span>{Math.round(ing.kcal)} cal</span>
                    <span>P: {Math.round(ing.protein_g)}g</span>
                    <span>C: {Math.round(ing.carbs_g)}g</span>
                    <span>F: {Math.round(ing.fat_g)}g</span>
                  </div>

                  {/* Delete Button */}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => deleteIngredient(idx)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Remove
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Cooking Essentials - Editable */}
        <Card className="mt-6 border-dashed">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-base text-gray-600">Cooking Essentials (non-scaling)</CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const newEssential = {
                  food_bank_id: foodBankItems[0]?.id || '',
                  food_bank_name: foodBankItems[0]?.name || '',
                  grams: 10,
                  kcal: 0,
                  isNew: true
                };
                setRecipe({
                  ...recipe,
                  cooking_essentials: [...(recipe.cooking_essentials || []), newEssential]
                });
              }}
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Essential
            </Button>
          </CardHeader>
          <CardContent>
            {(!recipe.cooking_essentials || recipe.cooking_essentials.length === 0) ? (
              <p className="text-sm text-gray-400 italic">No cooking essentials. Click "Add Essential" to add oils, spices, etc.</p>
            ) : (
              <div className="space-y-3">
                {recipe.cooking_essentials.map((ess, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    {/* Food Bank Dropdown */}
                    <div className="flex-1">
                      <Select
                        value={ess.food_bank_id}
                        onValueChange={async (value) => {
                          try {
                            const response = await axios.put(
                              `${API}/library-recipes/${recipe.id}/cooking-essentials/${idx}`,
                              { food_bank_id: value, grams: ess.quantity || ess.grams || 10 }
                            );
                            const updatedRecipe = { ...recipe };
                            updatedRecipe.cooking_essentials[idx] = response.data.essential;
                            updatedRecipe.total_kcal = response.data.recipe_totals.kcal;
                            setRecipe(updatedRecipe);
                            toast.success('Essential updated');
                          } catch (error) {
                            toast.error('Failed to update essential');
                          }
                        }}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select item">
                            {ess.food_bank_name || foodBankItems.find(f => f.id === ess.food_bank_id)?.name || 'Select'}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent className="max-h-[300px]">
                          {foodBankItems.map(item => (
                            <SelectItem key={item.id} value={item.id}>
                              {item.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Quantity Input */}
                    <div className="w-24">
                      <Input
                        type="number"
                        value={ess.quantity || ess.grams || 0}
                        onChange={(e) => {
                          const updatedEssentials = [...recipe.cooking_essentials];
                          updatedEssentials[idx] = { ...ess, quantity: parseFloat(e.target.value) || 0 };
                          setRecipe({ ...recipe, cooking_essentials: updatedEssentials });
                        }}
                        onBlur={async (e) => {
                          try {
                            const response = await axios.put(
                              `${API}/library-recipes/${recipe.id}/cooking-essentials/${idx}`,
                              { food_bank_id: ess.food_bank_id, grams: parseFloat(e.target.value) || 1 }
                            );
                            const updatedRecipe = { ...recipe };
                            updatedRecipe.cooking_essentials[idx] = response.data.essential;
                            updatedRecipe.total_kcal = response.data.recipe_totals.kcal;
                            setRecipe(updatedRecipe);
                          } catch (error) {
                            toast.error('Failed to update');
                          }
                        }}
                        className="font-mono text-sm"
                      />
                    </div>
                    
                    {/* Unit Display */}
                    <div className="w-16 text-sm text-gray-500">
                      {ess.unit || 'g'}
                    </div>
                    
                    {/* Kcal */}
                    <div className="w-16 text-sm text-gray-600">
                      {Math.round(ess.kcal || 0)} cal
                    </div>
                    
                    {/* Delete Button */}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={async () => {
                        try {
                          await axios.delete(`${API}/library-recipes/${recipe.id}/cooking-essentials/${idx}`);
                          const updatedEssentials = recipe.cooking_essentials.filter((_, i) => i !== idx);
                          setRecipe({ ...recipe, cooking_essentials: updatedEssentials });
                          toast.success('Essential removed');
                        } catch (error) {
                          toast.error('Failed to remove');
                        }
                      }}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
            <p className="text-xs text-gray-400 mt-3">
              ℹ️ These ingredients don't scale with calorie targets (e.g., oil, salt, spices)
            </p>
          </CardContent>
        </Card>

        {/* Instructions - Editable */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Instructions</CardTitle>
          </CardHeader>
          <CardContent>
            <textarea
              value={recipe.instructions || ''}
              onChange={(e) => setRecipe({ ...recipe, instructions: e.target.value })}
              placeholder="Enter cooking instructions here..."
              className="w-full min-h-[200px] p-3 text-sm text-gray-700 border rounded-lg resize-y focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows={10}
            />
            <div className="mt-3 flex justify-end">
              <Button
                onClick={async () => {
                  try {
                    await axios.put(`${API}/library-recipes/${recipe.id}/metadata`, {
                      instructions: recipe.instructions
                    });
                    toast.success('Instructions saved!');
                  } catch (error) {
                    toast.error('Failed to save instructions');
                  }
                }}
                size="sm"
              >
                <Save className="w-4 h-4 mr-2" />
                Save Instructions
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Scale Dialog */}
      <Dialog open={showScaleDialog} onOpenChange={setShowScaleDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Scale Recipe Preview</DialogTitle>
            <DialogDescription>
              Review the scaled ingredients before applying
            </DialogDescription>
          </DialogHeader>

          {scalePreview && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="bg-blue-50 border border-blue-200 rounded p-4">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-sm text-gray-600">Original</p>
                    <p className="text-lg font-bold">{Math.round(scalePreview.original_calories)} cal</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Target</p>
                    <p className="text-lg font-bold">{Math.round(scalePreview.target_calories)} cal</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Actual (after rounding)</p>
                    <p className="text-lg font-bold text-[#1a61e8]">{Math.round(scalePreview.actual_calories)} cal</p>
                  </div>
                </div>
              </div>

              {/* Adjustments */}
              {scalePreview.adjustments.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded p-4">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                    <div className="flex-1">
                      <p className="font-semibold text-amber-900">Smart Rounding Applied:</p>
                      <ul className="mt-2 space-y-1 text-sm text-amber-800">
                        {scalePreview.adjustments.map((adj, idx) => (
                          <li key={idx}>
                            <strong>{adj.ingredient}:</strong> {adj.target}g → {adj.adjusted}g ({adj.reason})
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* Scaled Ingredients */}
              <div>
                <h3 className="font-semibold mb-2">Scaled Ingredients:</h3>
                <div className="space-y-2">
                  {scalePreview.scaled_ingredients.map((ing, idx) => (
                    <div key={idx} className="flex justify-between items-center text-sm border-b pb-2">
                      <span className="font-medium">{ing.display_text}</span>
                      <span className="text-gray-600">{ing.grams}g | {Math.round(ing.kcal)} cal</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* New Totals */}
              <div className="bg-[#1a61e8]/5 border border-[#1a61e8]/20 rounded p-4">
                <p className="font-semibold mb-2">New Recipe Totals:</p>
                <div className="grid grid-cols-4 gap-2 text-sm">
                  <div>
                    <p className="text-gray-600">Calories</p>
                    <p className="font-bold">{Math.round(scalePreview.totals.kcal)}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Protein</p>
                    <p className="font-bold">{Math.round(scalePreview.totals.protein_g)}g</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Carbs</p>
                    <p className="font-bold">{Math.round(scalePreview.totals.carbs_g)}g</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Fat</p>
                    <p className="font-bold">{Math.round(scalePreview.totals.fat_g)}g</p>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setShowScaleDialog(false)}>
                  Cancel
                </Button>
                <Button onClick={applyScaling}>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Apply Scaling
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Ingredient Dialog */}
      <Dialog open={showAddIngredient} onOpenChange={setShowAddIngredient}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Ingredient</DialogTitle>
            <DialogDescription>
              Select an ingredient from the Food Bank and specify the amount
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Food Bank Selector */}
            <div>
              <Label>Ingredient</Label>
              <Select
                value={newIngredientFoodId}
                onValueChange={setNewIngredientFoodId}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select an ingredient..." />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {foodBankItems.map(item => (
                    <SelectItem key={item.id} value={item.id}>
                      {item.name} ({item.category})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Grams Input */}
            <div>
              <Label>Amount (grams)</Label>
              <Input
                type="number"
                value={newIngredientGrams}
                onChange={(e) => setNewIngredientGrams(e.target.value)}
                placeholder="100"
                min="1"
              />
            </div>

            {/* Actions */}
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => {
                  setShowAddIngredient(false);
                  setNewIngredientFoodId('');
                  setNewIngredientGrams('100');
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddIngredient}
                disabled={adding || !newIngredientFoodId}
              >
                <Plus className="w-4 h-4 mr-2" />
                {adding ? 'Adding...' : 'Add Ingredient'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
